import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { isEmpty } from 'ramda';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';

import { appActions } from '../../../modules/app';
import { companyActions } from '../../../modules/company';
import { modalsConstants } from '../../../modules/modals';
import { reviewActions } from '../../../modules/review';
import { auth } from '../../../utils/auth';
import { getCompanyRoute } from '../../../utils/helpers/routesBuilder';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import redChat from '../../assets/imgs/icons/chat-red.svg';
import { filterNames } from '../../constants/filters';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { routes } from '../../routes/routesConfig';
import { PageTitleVioletS } from '../adminPanel/adminLayout/adminCommonStyles';
import { Divider, GreyBtnS, LinkS, Text } from '../common';
import Tooltip from '../common/Tooltip';
import ConfirmationModal from '../modals/confirmation';

import { SETTINGS_TYPES } from './common/settingsComponents';
import {
  BaseEditCompanyBlockFooterS,
  CaptureWrapS,
  ContentWrapS,
  SettingsListWrapS,
  SettingsListItemS,
  SettingsContentWrapS,
  EditContentWrapper,
  BaseBlockStyled,
} from './companyEditS';
import CompanySettings from './CompanySettings';
import FulfillmentProgressBar from './fulfillmentProgressBar';

const { fetchCompanyItem, fetchInitialCompanyItem, updateCompany } = companyActions;
const { fetchCompanyReviews } = reviewActions;
const { getActiveFilters, getStaticCompanyValues } = appActions;

const CompanyEditComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { width: windowWidth } = useWindowSize();

  const activeFilters = useSelector((state) => state.app.activeFilters);
  const company = useSelector((state) => state.company.companyItem);
  const defaultPlaceholder = useSelector((state) => state.company.defaultPlaceholder);

  const pathSegments = location.pathname.split('/');
  const companyId = pathSegments.includes('admin') ? pathSegments[3] : pathSegments[2];
  const isAdmin = location.pathname.includes('/admin/');
  const isInitialCompany = location.pathname.includes('/initial-company');

  const [activeSettingType, setActiveSettingType] = useState('MAIN_SETTINGS');

  useEffect(() => {
    auth();
  }, []);

  useEffect(() => {
    if (isEmpty(activeFilters[filterNames.KANTONES])) {
      dispatch(getActiveFilters());
    }
    dispatch(getStaticCompanyValues());

    if (!isInitialCompany) {
      dispatch(fetchCompanyItem(companyId));
      dispatch(fetchCompanyReviews(companyId));
    } else {
      dispatch(fetchInitialCompanyItem(isInitialCompany));
    }
  }, [activeFilters, companyId, isInitialCompany, filterNames.KANTONES]);

  const handleUpdateCompany = (data) => {
    dispatch(updateCompany(data));
  };

  const handleConfirmationApproved = (id, { nextSettingsName }) => {
    document.dispatchEvent(new CustomEvent('onSaveUnsavedData', { detail: nextSettingsName }));
  };

  const handleConfirmationRejected = (id, { nextSettingsName }) => {
    setActiveSettingType(nextSettingsName);
  };

  const handleTabChange = (settingName) => {
    document.dispatchEvent(new CustomEvent('onTabChange', { detail: settingName }));
  };

  const renderHeader = () => {
    if (isAdmin) {
      return (
        <PageTitleVioletS>
          <Trans i18nKey="adminPanel.companies.companyProfile" />
        </PageTitleVioletS>
      );
    }

    return (
      <CaptureWrapS>
        {company.base && (
          <LinkS to={getCompanyRoute(company.id, company.name)}>
            <Text fontFamily="bold" color="red">
              <FontAwesomeIcon icon="chevron-left" />
            </Text>
            <Text fontFamily="bold">
              <Trans i18nKey="companyItem.goBackBtn" />
            </Text>
          </LinkS>
        )}
        <Trans i18nKey="companyEdit.capture" />
      </CaptureWrapS>
    );
  };

  const renderSettings = () => {
    if (windowWidth > SCREEN_SIZES.MEDIUM) {
      return (
        <>
          <Divider height="2px" style={{ marginBottom: '20px' }} />

          <DisplayFlexS>
            <SettingsListWrapS>
              {Object.keys(SETTINGS_TYPES).map((settingName) => (
                <SettingsListItem
                  key={settingName}
                  settingName={settingName}
                  isActive={activeSettingType === settingName}
                  onClick={() => handleTabChange(settingName)}
                />
              ))}
            </SettingsListWrapS>
            <SettingsContentWrapS>
              {SETTINGS_TYPES[activeSettingType].content({
                isAdmin,
                isInitialCompany,
                updateCompany: handleUpdateCompany,
                setActiveSettingType,
                defaultPlaceholder,
              })}
            </SettingsContentWrapS>
          </DisplayFlexS>
        </>
      );
    }

    return (
      <CompanySettings
        activeSettingType={activeSettingType}
        isAdmin={isAdmin}
        isInitialCompany={isInitialCompany}
        updateCompany={handleUpdateCompany}
        setActiveSettingType={setActiveSettingType}
        defaultPlaceholder={defaultPlaceholder}
      />
    );
  };

  return (
    <ContentWrapS isAdmin={isAdmin}>
      <ConfirmationModal
        confirmationName={modalsConstants.MODAL_TYPES.SAVE_CHANGES_COMPANY_PROFILE}
        onApproved={handleConfirmationApproved}
        onRejected={handleConfirmationRejected}
      />

      {renderHeader()}

      {/* An advertising banner block can be placed here. 
        In order to place it you need to use a styled component AdvertismentBlockS 
        and place the AdvertismentBanner component in it. */}

      <EditContentWrapper>
        <BaseBlockStyled>
          <FulfillmentProgressBar percentFulfillment={company.percentFulfillment} />
          {renderSettings()}
        </BaseBlockStyled>
      </EditContentWrapper>

      {isAdmin && (
        <BaseEditCompanyBlockFooterS>
          <Link to={`${routes.ADMIN.BASE}${routes.ADMIN.COMPANIES}`}>
            <GreyBtnS padding="5px 50px" style={{ marginRight: 20 }}>
              <Trans i18nKey="adminPanel.modals.companyDeleteConfirmation.cancelBtn" />
            </GreyBtnS>
          </Link>
        </BaseEditCompanyBlockFooterS>
      )}
    </ContentWrapS>
  );
};

const SettingsListItem = ({ settingName, isActive, onClick }) => {
  return (
    <SettingsListItemS onClick={onClick} selected={isActive}>
      {t(SETTINGS_TYPES[settingName].title)}
      <br />
      {SETTINGS_TYPES[settingName].withWarningText && (
        <Tooltip
          label={
            <Text weight="400" color="red">
              <img src={redChat} alt="warn-icon" style={{ transform: 'translateY(6px)', marginRight: '4px' }} />
              {t('companyEdit.rateWarningMsg.title')}
            </Text>
          }
          tooltipText={t('companyEdit.rateWarningMsg.desc')}
        />
      )}
    </SettingsListItemS>
  );
};

export default CompanyEditComponent;
